/**
 * FullSyncButton — löst eine vollständige Synchronisation des ECHTEN Kalender-Zustands
 * (Rates aus PropertyAriDay, Availability aus Buchungen) nach Channex aus. Zeigt vor dem
 * Senden eine Zustands-Vorschau, damit der Operator sieht, was rausgeht (u.a. ob flach).
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { toApiError, useApi } from '@/shared/api';
import { addTrackedAriOperations } from './ariOperationStorage';
import { FullSyncPreviewDetails } from './FullSyncPreviewDetails';
import { useFullSyncPreview } from './useFullSyncPreview';

interface FullSyncButtonProps {
  propertyId: string;
  /** Der Basispreis der Vorschau ist Geld — er wird in der Währung des Apartments gezeigt. */
  currency?: string;
}

export function FullSyncButton({ propertyId, currency = 'EUR' }: FullSyncButtonProps) {
  return <FullSyncButtonForProperty key={propertyId} propertyId={propertyId} currency={currency} />;
}

function FullSyncButtonForProperty({ propertyId, currency }: Required<FullSyncButtonProps>) {
  const { t } = useTranslation();
  const api = useApi();
  const [open, setOpen] = useState(false);
  const previewState = useFullSyncPreview(propertyId, () =>
    api.channex.fullSyncPreview(propertyId),
  );
  const preview = previewState.query;

  const run = useMutation({
    mutationFn: () => api.channex.fullSync(propertyId),
    onSuccess: (result) => {
      addTrackedAriOperations(result.propertyId, result.operationIds);
      toast.success(
        t('admin.ari.fullSync.success', {
          rangeCount: result.rangeCount,
          totalDays: result.totalDays,
        }),
      );
      setOpen(false);
    },
    onError: async (err) => {
      const e = await toApiError(err);
      toast.error([e.title, e.detail].filter(Boolean).join(' — ') || t('admin.ari.fullSync.error'));
    },
  });

  const openPreview = () => {
    setOpen(true);
    void previewState.refresh();
  };

  const setDialogOpen = (value: boolean) => {
    if (run.isPending) return;
    setOpen(value);
    if (!value) {
      previewState.reset();
    }
  };

  const p = previewState.ready && !preview.isError ? preview.data : undefined;
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        leftIcon={<RefreshCw size={13} strokeWidth={2} />}
        onClick={openPreview}
      >
        {t('admin.ari.fullSync.trigger')}
      </Button>
      <Dialog open={open} onOpenChange={setDialogOpen}>
        <DialogContent showClose={!run.isPending}>
          <DialogHeader>
            <DialogTitle>{t('admin.ari.fullSync.title')}</DialogTitle>
            <DialogDescription className="text-xs">
              {t('admin.ari.fullSync.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            {preview.isPaused && (
              <p className="text-ink-soft text-xs" role="status">
                {t('admin.ari.fullSync.offlineWaiting')}
              </p>
            )}
            {preview.isFetching && !preview.isPaused && (
              <p className="text-ink-soft text-xs" role="status">
                {t('admin.ari.fullSync.loading')}
              </p>
            )}
            {preview.isError && !preview.isFetching && (
              <div className="space-y-2" role="alert">
                <p className="text-sealwax text-xs">{t('admin.ari.fullSync.previewError')}</p>
                <Button
                  size="sm"
                  variant="ghost"
                  className="min-h-11"
                  loading={preview.isFetching}
                  onClick={() => void previewState.refresh()}
                >
                  {t('common.retry')}
                </Button>
              </div>
            )}
            {p && <FullSyncPreviewDetails preview={p} currency={currency} />}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={run.isPending}>
              {t('admin.ari.fullSync.cancel')}
            </Button>
            <Button
              variant="brand"
              onClick={() => run.mutate()}
              loading={run.isPending}
              disabled={!p || !previewState.isOnline || preview.isPaused || p.daysWithoutPrice > 0}
            >
              {t('admin.ari.fullSync.synchronize')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
